import { createRequest } from '@/services/request';

const request = createRequest();

export async function queryProjects(params?: API.QueryProjectsParams) {
  return request<API.QueryProjectsResponse>('/bff/software-project', {
    method: 'GET',
    params,
  });
}

export async function createProject(body: API.CreateProjectRequest) {
  return request<API.Project>('/bff/software-project', {
    method: 'POST',
    data: body,
  });
}

export async function queryMyParticipatedProjects(
  params?: API.QueryProjectsParams,
) {
  return request<API.QueryProjectsResponse>(
    '/bff/software-project/my-participated-projects',
    {
      method: 'GET',
      params,
    },
  );
}

export async function queryMyFollowedProjects(
  params?: API.QueryProjectsParams,
) {
  return request<API.QueryProjectsResponse>(
    '/bff/software-project/my-followed-projects',
    {
      method: 'GET',
      params,
    },
  );
}

export async function followProject(uuid: string) {
  return request<void>(`/bff/software-project/${uuid}/follow`, {
    method: 'POST',
  });
}

export async function unfollowProject(uuid: string) {
  return request<void>(`/bff/software-project/${uuid}/follow`, {
    method: 'DELETE',
  });
}

export async function generateProjectIdentifier(
  body: API.GenerateProjectIdentifierRequest,
) {
  return request<API.GenerateProjectIdentifierResponse>(
    '/bff/software-project/generate-identifier',
    {
      method: 'POST',
      data: body,
    },
  );
}

export async function queryProjectGroups() {
  return request<API.ProjectGroup[]>('/bff/software-project/groups', {
    method: 'GET',
  });
}

export async function createProjectGroup(body: API.CreateProjectGroupRequest) {
  return request<API.ProjectGroup>('/bff/software-project/groups', {
    method: 'POST',
    data: body,
  });
}

export async function updateProjectGroupByUuid(
  uuid: string,
  body: API.UpdateProjectGroupRequest,
) {
  return request<API.ProjectGroup>(`/bff/software-project/groups/${uuid}`, {
    method: 'PATCH',
    data: body,
  });
}

export async function deleteProjectGroupByUuid(uuid: string) {
  return request<void>(`/bff/software-project/groups/${uuid}`, {
    method: 'DELETE',
  });
}

export async function importProjectsByExcel(formData: FormData) {
  return request<API.ImportProjectsResponse>('/bff/software-project/import', {
    method: 'POST',
    data: formData,
    requestType: 'form',
  });
}

export async function updateProjectByUuid(
  uuid: string,
  body: API.UpdateProjectRequest,
) {
  return request<API.Project>(`/bff/software-project/${uuid}`, {
    method: 'PATCH',
    data: body,
  });
}

export async function batchMoveProjectsToGroup(
  body: API.BatchMoveProjectsRequest,
) {
  return request<API.BatchMoveProjectsResponse>(
    '/bff/software-project/batch/group',
    {
      method: 'PATCH',
      data: body,
    },
  );
}

export async function patchProjectByUuid(
  uuid: string,
  body: API.UpdateProjectRequest,
) {
  return request<API.Project>(`/bff/software-project/${uuid}`, {
    method: 'PATCH',
    data: body,
  });
}

export async function deleteProjectByUuid(uuid: string) {
  return request<void>(`/bff/software-project/${uuid}`, {
    method: 'DELETE',
  });
}

export async function queryProjectDeletionImpact(uuid: string) {
  return request<{
    projectUuid: string;
    canDelete: boolean;
    ownedApplicationCount: number;
    applications: Array<{
      uuid: string;
      name: string;
      identifier: string;
      referenceCount: number;
    }>;
  }>(`/bff/software-project/${uuid}/deletion-impact`, { method: 'GET' });
}

export async function queryProjectByUuid(uuid: string) {
  return request<API.Project>(`/bff/software-project/${uuid}`, {
    method: 'GET',
  });
}

export async function checkProjectIdentifier(
  identifier: string,
  excludeUuid?: string,
) {
  return request<{ exists: boolean }>(
    `/bff/software-project/check-identifier/${identifier}`,
    {
      method: 'GET',
      params: excludeUuid ? { excludeUuid } : undefined,
    },
  );
}

export async function queryAbApplication(
  params?: API.QueryAbApplicationParams,
) {
  return request<API.QueryAbApplicationResponse>('/bff/ab-application', {
    method: 'GET',
    params,
  });
}

export async function createAbApplication(
  body: API.CreateAbApplicationRequest,
) {
  return request<API.AbApplication>('/bff/ab-application', {
    method: 'POST',
    data: body,
  });
}

export async function updateAbApplication(
  uuid: string,
  body: API.UpdateAbApplicationRequest,
) {
  return request<API.AbApplication>(`/bff/ab-application/${uuid}`, {
    method: 'PATCH',
    data: body,
  });
}

export async function deleteAbApplication(uuid: string) {
  return request<void>(`/bff/ab-application/${uuid}`, {
    method: 'DELETE',
  });
}

export async function queryApplicationReferenceCandidates(params: {
  targetProjectUuid: string;
  page?: number;
  limit?: number;
  name?: string;
}) {
  return request<API.QueryAbApplicationResponse>(
    '/bff/ab-application/reference-candidates',
    { method: 'GET', params },
  );
}

export async function referenceApplication(uuid: string, projectUuid: string) {
  return request<API.AbApplication>(`/bff/ab-application/${uuid}/references`, {
    method: 'POST',
    data: { projectUuid },
  });
}

export async function unreferenceApplication(
  uuid: string,
  projectUuid: string,
) {
  return request<void>(
    `/bff/ab-application/${uuid}/references/${projectUuid}`,
    {
      method: 'DELETE',
    },
  );
}

export async function moveApplication(uuid: string, targetProjectUuid: string) {
  return request<API.AbApplication>(`/bff/ab-application/${uuid}/move`, {
    method: 'POST',
    data: { targetProjectUuid },
  });
}

export async function checkAbApplicationIdentifier(
  identifier: string,
  excludeUuid?: string,
) {
  return request<{ exists: boolean }>(
    `/bff/ab-application/check-identifier/${identifier}`,
    {
      method: 'GET',
      params: excludeUuid ? { excludeUuid } : undefined,
    },
  );
}

export async function queryAbCodebases(params?: API.QueryAbCodebaseParams) {
  return request<API.QueryAbCodebaseResponse>('/bff/ab-codebases', {
    method: 'GET',
    params,
  });
}

export async function createAbCodebase(body: API.CreateAbCodebaseRequest) {
  return request<API.AbCodebase>('/bff/ab-codebases', {
    method: 'POST',
    data: body,
  });
}

export async function updateAbCodebase(
  uuid: string,
  body: API.UpdateAbCodebaseRequest,
) {
  return request<API.AbCodebase>(`/bff/ab-codebases/${uuid}`, {
    method: 'PATCH',
    data: body,
  });
}

export async function deleteAbCodebase(uuid: string) {
  return request<void>(`/bff/ab-codebases/${uuid}`, {
    method: 'DELETE',
  });
}

export async function queryOperationLog(params?: API.QueryOperationLogParams) {
  return request<API.QueryOperationLogResponse>('/bff/operation-log', {
    method: 'GET',
    params,
  });
}

export async function queryProjectStatistics(projectUuid: string) {
  return request<API.ProjectStatistics>('/bff/statistic/project', {
    method: 'GET',
    params: { projectUuid },
  });
}

export async function queryDevelopmentAgents(
  params?: API.QueryDevelopmentAgentsParams,
) {
  return request<API.QueryDevelopmentAgentsResponse>('/bff/dev-agents', {
    method: 'GET',
    params,
  });
}

export async function createDevelopmentAgent(
  body: API.CreateDevelopmentAgentRequest,
) {
  return request<API.DevelopmentAgent>('/bff/dev-agents', {
    method: 'POST',
    data: body,
  });
}

export async function updateDevelopmentAgentByUuid(
  uuid: string,
  body: API.UpdateDevelopmentAgentRequest,
) {
  return request<API.DevelopmentAgent>(`/bff/dev-agents/${uuid}`, {
    method: 'PATCH',
    data: body,
  });
}

export async function queryDevelopmentAgentByUuid(uuid: string) {
  return request<API.DevelopmentAgent>(`/bff/dev-agents/${uuid}`, {
    method: 'GET',
  });
}

export async function deleteDevelopmentAgentByUuid(uuid: string) {
  return request<void>(`/bff/dev-agents/${uuid}`, {
    method: 'DELETE',
  });
}

export async function queryDevelopmentAgentNodes() {
  return request<API.QueryDevelopmentAgentNodesResponse>(
    '/bff/dev-agent-nodes',
    {
      method: 'GET',
    },
  );
}

export async function createDevelopmentAgentNode(
  body: API.CreateDevelopmentAgentNodeRequest,
) {
  return request<API.DevelopmentAgentNode>('/bff/dev-agent-nodes', {
    method: 'POST',
    data: body,
  });
}

export async function queryDevelopmentAgentNodeByUuid(uuid: string) {
  return request<API.DevelopmentAgentNodeDetail>(
    `/bff/dev-agent-nodes/${uuid}`,
    {
      method: 'GET',
    },
  );
}

export async function deleteDevelopmentAgentNodeByUuid(uuid: string) {
  return request<void>(`/bff/dev-agent-nodes/${uuid}`, {
    method: 'DELETE',
  });
}

export async function scanDevelopmentAgentNode(uuid: string) {
  return request<API.DevelopmentAgentNodeDetail>(
    `/bff/dev-agent-nodes/${uuid}/scan`,
    {
      method: 'POST',
    },
  );
}

export async function initializeDevelopmentAgentNode(uuid: string) {
  return request<API.DevelopmentAgentNode>(
    `/bff/dev-agent-nodes/${uuid}/initialize`,
    {
      method: 'POST',
    },
  );
}

export async function queryProjectBoundDevelopmentAgents(
  projectUuid: string,
  params?: API.QueryProjectDevelopmentAgentsParams,
) {
  return request<API.QueryDevelopmentAgentsResponse>(
    `/bff/dev-agents/project/${projectUuid}/bindings`,
    {
      method: 'GET',
      params,
    },
  );
}

export async function planDevelopmentAgentJobsByProject(
  projectUuid: string,
  body: API.PlanDevelopmentAgentJobsRequest,
) {
  return request<API.PlanDevelopmentAgentJobsResponse>(
    `/bff/dev-agents/project/${projectUuid}/plan-jobs`,
    {
      method: 'POST',
      data: body,
    },
  );
}

export async function queryProjectCandidateDevelopmentAgents(
  projectUuid: string,
  params?: API.QueryProjectDevelopmentAgentsParams,
) {
  return request<API.QueryDevelopmentAgentsResponse>(
    `/bff/dev-agents/project/${projectUuid}/candidates`,
    {
      method: 'GET',
      params,
    },
  );
}

export async function bindDevelopmentAgentToProject(
  projectUuid: string,
  agentUuid: string,
) {
  return request<API.BindDevelopmentAgentToProjectResponse>(
    `/bff/dev-agents/project/${projectUuid}/agents/${agentUuid}/bind`,
    {
      method: 'POST',
    },
  );
}

export async function unbindDevelopmentAgentFromProject(
  projectUuid: string,
  agentUuid: string,
) {
  return request<void>(
    `/bff/dev-agents/project/${projectUuid}/agents/${agentUuid}/bind`,
    {
      method: 'DELETE',
    },
  );
}

export async function createDevelopmentAgentForProject(
  projectUuid: string,
  body: API.CreateDevelopmentAgentRequest,
) {
  return request<API.DevelopmentAgent>(
    `/bff/dev-agents/project/${projectUuid}/agents`,
    {
      method: 'POST',
      data: body,
    },
  );
}

export async function queryDevelopmentAgentProjects(uuid: string) {
  return request<API.DevelopmentAgentProject[]>(
    `/bff/dev-agents/${uuid}/projects`,
    {
      method: 'GET',
    },
  );
}

export async function replaceDevelopmentAgentProjects(
  uuid: string,
  body: API.ReplaceDevelopmentAgentProjectsRequest,
) {
  return request<{ projectUuids: string[] }>(
    `/bff/dev-agents/${uuid}/projects`,
    {
      method: 'PUT',
      data: body,
    },
  );
}

export async function queryDevelopmentAgentSecrets(uuid: string) {
  return request<API.DevelopmentAgentSecret[]>(
    `/bff/dev-agents/${uuid}/secrets`,
    {
      method: 'GET',
    },
  );
}

export async function checkDevelopmentAgentHealth(uuid: string) {
  return request<{
    healthy: boolean;
    statusCode: number | null;
    latencyMs: number;
  }>(`/bff/dev-agents/${uuid}/health`, { method: 'GET' });
}

export async function replaceDevelopmentAgentSecrets(
  uuid: string,
  body: API.ReplaceDevelopmentAgentSecretsRequest,
) {
  return request<{ keys: string[] }>(`/bff/dev-agents/${uuid}/secrets`, {
    method: 'PUT',
    data: body,
  });
}

export async function getDevelopmentAgentBootstrapAuth(uuid: string) {
  return request<{
    authFilePath: string;
    config: {
      agentUuid: string;
      serverBaseUrl: string;
      registrationToken: string;
      workerAuthToken?: string;
      workerBaseUrl?: string;
    };
  }>(`/bff/dev-agents/${uuid}/bootstrap-auth`, {
    method: 'GET',
  });
}

export async function initializeDevelopmentAgent(uuid: string) {
  return request<{
    filePath: string;
    agentUuid: string;
    serverBaseUrl: string;
    workerBaseUrl: string;
    registered: boolean;
    updatedAt: string;
  }>(`/bff/dev-agents/${uuid}/initialize`, {
    method: 'POST',
  });
}

export async function queryDevelopmentAgentJobs(
  params?: API.QueryDevelopmentAgentJobsParams,
) {
  return request<API.QueryDevelopmentAgentJobsResponse>('/bff/agent-tasks', {
    method: 'GET',
    params,
  });
}

export async function createDevelopmentAgentJob(
  body: API.CreateDevelopmentAgentJobRequest,
) {
  return request<API.DevelopmentAgentJob>('/bff/agent-tasks', {
    method: 'POST',
    data: body,
  });
}

export async function updateDevelopmentAgentJobByUuid(
  uuid: string,
  body: API.UpdateDevelopmentAgentJobRequest,
) {
  return request<API.DevelopmentAgentJob>(`/bff/agent-tasks/${uuid}`, {
    method: 'PATCH',
    data: body,
  });
}

export async function updateDevelopmentAgentJobStatus(
  uuid: string,
  body: API.UpdateDevelopmentAgentJobStatusRequest,
) {
  return request<API.DevelopmentAgentJob>(`/bff/agent-tasks/${uuid}/status`, {
    method: 'PATCH',
    data: body,
  });
}

export const queryAgentTasks = queryDevelopmentAgentJobs;
export const createAgentTask = createDevelopmentAgentJob;

export async function generateProjectBlueprint(
  body: API.GenerateProjectBlueprintRequest,
) {
  return request<API.ProjectBlueprintResponse>(
    '/bff/dev-agent-blueprints/generate',
    {
      method: 'POST',
      data: body,
    },
  );
}

export async function queryLatestProjectBlueprint(projectUuid: string) {
  return request<API.ProjectBlueprintResponse>(
    `/bff/dev-agent-blueprints/projects/${projectUuid}/latest`,
    {
      method: 'GET',
      // The blueprint page treats 404 as "no blueprint yet" and renders an empty state.
      skipErrorHandler: true,
    },
  );
}

export async function applyProjectBlueprint(
  blueprintUuid: string,
  body: API.ApplyProjectBlueprintRequest,
) {
  return request<API.ProjectBlueprintApplyResponse>(
    `/bff/dev-agent-blueprints/${blueprintUuid}/apply`,
    {
      method: 'POST',
      data: body,
    },
  );
}

// ==================== GitLab API ====================

export async function queryGitlabCommits(
  projectId: number,
  params?: { page?: number; perPage?: number; ref?: string },
) {
  return request<API.CommitHistoryResponse>(
    `/bff/gitlab/projects/${projectId}/commits`,
    { method: 'GET', params },
  );
}

export async function queryGitlabBranches(projectId: number) {
  return request<API.BranchInfo[]>(
    `/bff/gitlab/projects/${projectId}/branches`,
    { method: 'GET' },
  );
}

export async function queryGitlabRepository(projectId: number) {
  return request<API.RepositoryInfo>(
    `/bff/gitlab/projects/${projectId}/repository`,
    { method: 'GET' },
  );
}

export async function queryGitlabAccessTokens(projectId: number) {
  return request<API.ProjectAccessTokenInfo[]>(
    `/bff/gitlab/projects/${projectId}/access-tokens`,
    { method: 'GET' },
  );
}

export async function createGitlabAccessToken(
  projectId: number,
  body: {
    tokenName: string;
    scopes: string[];
    expiresAt?: string;
    accessLevel?: number;
  },
) {
  return request<{ token: string; id: number }>(
    `/bff/gitlab/projects/${projectId}/access-tokens`,
    { method: 'POST', data: body },
  );
}

export async function revokeGitlabAccessToken(
  projectId: number,
  tokenId: number,
) {
  return request<void>(
    `/bff/gitlab/projects/${projectId}/access-tokens/${tokenId}`,
    { method: 'DELETE' },
  );
}

export async function queryGitlabReadme(projectId: number, ref?: string) {
  return request<{ content: string | null }>(
    `/bff/gitlab/projects/${projectId}/readme`,
    { method: 'GET', params: ref ? { ref } : undefined },
  );
}

export async function syncGitlabCodebase(body: API.SyncGitlabCodebaseRequest) {
  return request<API.SyncGitlabCodebaseResponse>('/bff/gitlab/sync', {
    method: 'POST',
    data: body,
  });
}

export async function queryGitlabCodebaseMembers(codebaseUuid: string) {
  return request<API.GitlabCodebaseMembersResponse>(
    `/bff/gitlab/codebases/${codebaseUuid}/members`,
    { method: 'GET' },
  );
}

export async function updateGitlabCodebaseMembers(
  codebaseUuid: string,
  body: API.UpdateGitlabCodebaseMembersRequest,
) {
  return request<API.GitlabCodebaseMembersResponse>(
    `/bff/gitlab/codebases/${codebaseUuid}/members`,
    { method: 'PUT', data: body },
  );
}

export async function initializeProject(body: API.InitializeProjectRequest) {
  return request<API.InitializeProjectResponse>(
    '/bff/gitlab/initialize-project',
    {
      method: 'POST',
      data: body,
    },
  );
}

export async function migrateGitlabCodebase(body: API.MigrateCodebaseRequest) {
  return request<API.MigrateCodebaseResponse>('/bff/gitlab/migrate-codebase', {
    method: 'POST',
    data: body,
  });
}

export async function triggerGitlabPipeline(projectId: number, ref: string) {
  return request<API.PipelineInfo>(
    `/bff/gitlab/projects/${projectId}/pipelines`,
    {
      method: 'POST',
      data: { ref },
    },
  );
}

export async function queryGitlabPipelines(
  projectId: number,
  params?: { page?: number; perPage?: number; ref?: string },
) {
  return request<API.PipelineInfo[]>(
    `/bff/gitlab/projects/${projectId}/pipelines`,
    {
      method: 'GET',
      params,
    },
  );
}

export async function queryGitlabPipelineJobs(
  projectId: number,
  pipelineId: number,
) {
  return request<API.JobInfo[]>(
    `/bff/gitlab/projects/${projectId}/pipelines/${pipelineId}/jobs`,
    { method: 'GET' },
  );
}

export async function queryGitlabJobLog(projectId: number, jobId: number) {
  return request<{ log: string }>(
    `/bff/gitlab/projects/${projectId}/jobs/${jobId}/log`,
    {
      method: 'GET',
    },
  );
}

export async function queryCommitAnalysisCodebases(projectUuid: string) {
  return request<
    Array<
      Pick<
        API.AbCodebase,
        | 'uuid'
        | 'name'
        | 'identifier'
        | 'repoUrl'
        | 'defaultBranch'
        | 'gitProjectId'
      >
    >
  >(`/bff/code-commit-analysis/project/${projectUuid}/codebases`, {
    method: 'GET',
  });
}

export async function generateCommitDailyReport(
  body: API.CommitDailyReportRequest,
) {
  return request<API.CommitDailyReportResponse>(
    '/bff/code-commit-analysis/daily-report/generate',
    {
      method: 'POST',
      data: body,
    },
  );
}

export async function generateCommitChangelog(
  body: API.CommitChangelogRequest,
) {
  return request<API.CommitChangelogResponse>(
    '/bff/code-commit-analysis/changelog/generate',
    {
      method: 'POST',
      data: body,
    },
  );
}

export async function queryProjectContributionFilters(
  projectUuid: string,
  params?: API.QueryProjectContributionParams,
) {
  return request<API.ProjectContributionFilters>(
    `/bff/code-contribution/project/${projectUuid}/filters`,
    {
      method: 'GET',
      params,
    },
  );
}

export async function queryProjectContributionOverview(
  projectUuid: string,
  params?: API.QueryProjectContributionParams,
) {
  return request<API.ProjectContributionOverview>(
    `/bff/code-contribution/project/${projectUuid}/overview`,
    {
      method: 'GET',
      params,
    },
  );
}

export async function queryProjectContributionHeatmap(
  projectUuid: string,
  params?: API.QueryProjectContributionParams,
) {
  return request<API.ProjectContributionHeatmap>(
    `/bff/code-contribution/project/${projectUuid}/heatmap`,
    {
      method: 'GET',
      params,
    },
  );
}

export async function queryProjectContributionRankings(
  projectUuid: string,
  params?: API.QueryProjectContributionParams,
) {
  return request<API.ProjectContributionRankings>(
    `/bff/code-contribution/project/${projectUuid}/rankings`,
    {
      method: 'GET',
      params,
    },
  );
}

export async function queryProjectContributionTimeline(
  projectUuid: string,
  params?: API.QueryProjectContributionParams,
) {
  return request<API.ProjectContributionTimeline>(
    `/bff/code-contribution/project/${projectUuid}/timeline`,
    {
      method: 'GET',
      params,
    },
  );
}

export async function queryProjectContributionAudit(
  projectUuid: string,
  body: {
    codebaseUuid: string;
    commitIds: string[];
    branch?: string;
    commits?: Array<{ id: string; title: string; message: string }>;
  },
) {
  return request<unknown>(
    `/bff/code-contribution/project/${projectUuid}/audit`,
    {
      method: 'POST',
      data: body,
    },
  );
}

export async function queryTenantContributionFilters(
  params?: API.QueryTenantContributionParams,
) {
  return request<API.TenantContributionFilters>(
    '/bff/code-contribution/tenant/filters',
    {
      method: 'GET',
      params,
    },
  );
}

export async function queryTenantContributionOverview(
  params?: API.QueryTenantContributionParams,
) {
  return request<API.TenantContributionOverview>(
    '/bff/code-contribution/tenant/overview',
    {
      method: 'GET',
      params,
    },
  );
}

export async function queryTenantContributionHeatmap(
  params?: API.QueryTenantContributionParams,
) {
  return request<API.TenantContributionHeatmap>(
    '/bff/code-contribution/tenant/heatmap',
    {
      method: 'GET',
      params,
    },
  );
}

export async function queryTenantContributionRankings(
  params?: API.QueryTenantContributionParams,
) {
  return request<API.TenantContributionRankings>(
    '/bff/code-contribution/tenant/rankings',
    {
      method: 'GET',
      params,
    },
  );
}

export async function queryTenantContributionTimeline(
  params?: API.QueryTenantContributionParams,
) {
  return request<API.ProjectContributionTimeline>(
    '/bff/code-contribution/tenant/timeline',
    {
      method: 'GET',
      params,
    },
  );
}

export async function queryTenantUpgradeFeed(
  params?: API.QueryTenantContributionParams,
) {
  return request<API.TenantUpgradeFeed>(
    '/bff/code-contribution/tenant/upgrade-feed',
    {
      method: 'GET',
      params,
    },
  );
}

export async function queryProjectOverviewActivities(
  projectUuid: string,
  params?: {
    page?: number;
    limit?: number;
    stage?: API.ProjectOverviewStage;
    action?: string;
  },
) {
  return request<API.ProjectOverviewActivityPage>(
    `/bff/software-project/${encodeURIComponent(projectUuid)}/overview/activities`,
    {
      method: 'GET',
      params,
    },
  );
}
